import type { ComponentDocs } from '../component-docs.js';

const COLUMNS = [
  { value: 'name', label: 'Name' },
  { value: 'account', label: 'Account' },
  { value: 'stage', label: 'Stage' },
  { value: 'owner', label: 'Owner' },
  { value: 'amount', label: 'Amount' },
  { value: 'close_date', label: 'Close date' },
];

const REGIONS = [
  { value: 'north', label: 'North' },
  { value: 'south', label: 'South' },
  { value: 'east', label: 'East' },
  { value: 'west', label: 'West' },
  { value: 'central', label: 'Central' },
  { value: 'overseas', label: 'Overseas' },
];

/**
 * What `<caos-ordered-picker>` says about itself.
 *
 * THE LINE THIS PIECE SITS ON IS ORDER. Choosing several values where the order means nothing is the
 * picklist field with `multiple` — a group of checkboxes over the same declared list. Reach for this
 * one when the order of what is chosen is itself part of the answer: which columns a list shows, left
 * to right; which entries are pinned, top to bottom.
 *
 * THE VALUE IS A PROPERTY. `options`, `value` and `locked` are lists, and a list cannot be written in
 * an attribute, so every example below sets them as properties; the labels, limits and message are
 * ordinary attributes. A change is reported as a plain `change` with nothing in it — `value` has
 * already been updated when it fires, and reading it is the whole of handling it.
 */
const docs: ComponentDocs = {
  aliases: ['dual listbox', 'dueling picklist', 'transfer list', 'shuttle', 'list builder', 'column chooser', 'sortable multi-select'],

  answers: [
    'choose which columns a list shows, and in what order',
    'pin some entries in a chosen order and leave the rest as they were',
    'let somebody pick several values and rank them',
    'keep some values chosen that nobody may remove',
    'allow at least two and at most four choices',
  ],

  // Configuration a person arranges on a page of an app or a step of a flow; not a record value.
  placements: ['appPage', 'screen'],

  inputs: [
    {
      name: 'options',
      // No attribute: a list of options cannot be written in one.
      type: 'json',
      description:
        'Everything that could be chosen, as { value, label, qualifier? } objects or plain strings. The available ' +
        'list shows the ones not chosen, in this order. A qualifier is a quieter second line under the label.',
    },
    {
      name: 'value',
      type: 'json',
      description:
        'What is chosen, as a list of option values in order. It is written back on every change. A value that ' +
        'is not among the options stays in the chosen list, marked, until somebody removes it.',
    },
    {
      name: 'locked',
      type: 'json',
      description:
        'Values that are chosen and cannot be removed. Any not already in value are added to its end; each is ' +
        'drawn with a lock, and Remove refuses it.',
    },
    {
      name: 'min',
      attribute: 'min',
      property: true,
      type: 'number',
      description:
        'The fewest values that make a complete choice. A removal below it is allowed — swapping one entry for ' +
        'another passes through one too few — but the picker says how many are needed until it holds again.',
    },
    {
      name: 'max',
      attribute: 'max',
      property: true,
      type: 'number',
      description: 'The most values that can be chosen. Adding stops there, and the picker says why.',
    },
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description: 'What is being chosen, drawn above both lists and read as the first half of each list\'s name.',
    },
    {
      name: 'availableLabel',
      attribute: 'available-label',
      type: 'text',
      description: 'The heading over the list of what could be added.',
      default: 'Available',
    },
    {
      name: 'chosenLabel',
      attribute: 'chosen-label',
      type: 'text',
      description: 'The heading over the chosen list — the place to say what its order means.',
      default: 'Chosen',
    },
    {
      name: 'message',
      attribute: 'message',
      type: 'text',
      description: 'Helper text under the lists. A minimum or maximum that is in play says its own sentence instead.',
    },
    {
      name: 'disabled',
      attribute: 'disabled',
      property: true,
      type: 'boolean',
      description: 'The choice may be read but not changed: both lists and every button are refused.',
      default: false,
    },
  ],

  events: [
    {
      name: 'change',
      when:
        'An entry is added, removed or moved — by a button, the keyboard or a double-click — after value has been ' +
        'written back. Setting options, value or locked raises nothing.',
      carries: 'nothing — read value off the element.',
    },
  ],

  state: [],

  slots: [],

  tokens: [
    { token: '--caos-font', controls: 'The typeface of the label, the headings, the entries and the message.' },
    { token: '--caos-text-body', controls: 'The size of the label above the lists.' },
    { token: '--caos-text-row', controls: 'The size of each entry.' },
    { token: '--caos-space-1', controls: 'The padding inside each list, and the gap between a heading and its list.' },
    { token: '--caos-space-2', controls: 'The gap between the lists and the buttons, and the padding of each entry.' },
    { token: '--caos-radius', controls: 'The corners of the lists and the buttons.' },
    { token: '--caos-radius-sm', controls: 'The corners of an entry\'s highlight.' },
    { token: '--caos-color-border', controls: 'The edge of the lists and the buttons.' },
    { token: '--caos-color-border-strong', controls: 'The edge of a button under the pointer.' },
    { token: '--caos-color-surface', controls: 'The fill of the lists.' },
    { token: '--caos-color-surface-2', controls: 'The fill of the buttons, and of an entry under the pointer.' },
    { token: '--caos-state-selected-bg', controls: 'The fill of the highlighted entry in each list.' },
    { token: '--caos-color-text', controls: 'The entry labels and the button chevrons.' },
    { token: '--caos-color-text-muted', controls: 'The label, the headings, the position numbers, qualifiers, the lock and the message.' },
    { token: '--caos-color-danger', controls: 'The mark on a value that is not among the options, and a minimum not yet met.' },
    { token: '--caos-focus-ring', controls: 'The ring on a list or a button reached by keyboard.' },
  ],

  parts: [
    { part: 'picker', is: 'the whole piece' },
    { part: 'label', is: 'the label above the lists' },
    { part: 'heading', is: 'the heading over each list' },
    { part: 'listbox', is: 'each of the two lists' },
    { part: 'option', is: 'one entry in either list' },
    { part: 'rank', is: 'the position number of a chosen entry' },
    { part: 'button', is: 'the add, remove, move up and move down buttons' },
    { part: 'message', is: 'the line under the lists' },
  ],

  methods: [],

  placing:
    'Placed as an item in a page section, across the width it is given: the two lists share it equally, with the ' +
    'add and remove buttons between them and the move buttons beside the chosen list. Each list keeps a height of ' +
    'its own and scrolls past it, so a long set of options does not stretch the page. What the page owes it is ' +
    'the options, the current value, and something listening for a change.',

  whenToUse:
    'Several values are chosen AND their order is part of the answer — the columns a list shows, the entries ' +
    'pinned in a stack, a ranked shortlist — or some values have to stay chosen, or the number chosen has limits.',

  insteadUse: [
    {
      name: 'PicklistField',
      when:
        'The order means nothing: several values from one declared list is the picklist field with multiple set, a ' +
        'group of checkboxes that shows every option at once.',
    },
    { name: 'PillContainer', when: 'The chosen values are only being shown, as a row of tags.' },
    { name: 'LookupField', when: 'What is chosen is a record, searched for live rather than listed.' },
  ],

  accessibility:
    'Each list is a real `role="listbox"`, named by the label and its own heading and described by the message, ' +
    'with `aria-activedescendant` following the highlight so an entry is announced without focus leaving the ' +
    'list. Arrow Up, Arrow Down, Home and End move; Enter or Space moves the highlighted entry across; Alt with ' +
    'an arrow moves a chosen entry up or down. Every button carries an accessible name, a locked entry carries ' +
    'the words "Required, cannot be removed", and the minimum and maximum sentences are announced as they ' +
    'appear. The position numbers are hidden from assistive technology, which counts an option\'s place ' +
    'itself. Left to the author: option labels that tell entries apart, and headings that say what the lists ' +
    'mean when "Available" and "Chosen" do not.',

  examples: [
    {
      key: 'simple',
      label: 'Choosing and ordering',
      summary: 'Nothing chosen to begin with: columns picked from the list on the left and put in order on the right.',
      placement: { component: 'caos-ordered-picker', inputs: { label: 'Columns', options: COLUMNS } },
    },
    {
      key: 'chosen-already',
      label: 'Some already chosen',
      summary: 'Opens with three columns chosen, in the order they show, beside the ones that could still be added.',
      placement: {
        component: 'caos-ordered-picker',
        inputs: {
          label: 'Columns',
          availableLabel: 'Available columns',
          chosenLabel: 'Shown, left to right',
          options: COLUMNS,
          value: ['name', 'stage', 'amount'],
        },
      },
    },
    {
      key: 'locked',
      label: 'Values that stay chosen',
      summary: 'The record name is locked in: it carries a lock, and Remove refuses it however it is asked.',
      placement: {
        component: 'caos-ordered-picker',
        inputs: { label: 'Columns', options: COLUMNS, value: ['name', 'owner'], locked: ['name'] },
      },
    },
    {
      key: 'min-max',
      label: 'At least two, at most four',
      summary: 'With one region chosen the picker asks for another; at four, adding stops and it says why.',
      placement: {
        component: 'caos-ordered-picker',
        inputs: { label: 'Regions', options: REGIONS, value: ['north'], min: 2, max: 4 },
      },
    },
    {
      key: 'no-longer-an-option',
      label: 'A chosen value no longer offered',
      summary: 'A pinned entry whose package has gone stays in the chosen list, marked, until somebody takes it out.',
      placement: {
        component: 'caos-ordered-picker',
        inputs: {
          label: 'Edge tabs',
          availableLabel: 'Not pinned',
          chosenLabel: 'Pinned, in this order',
          options: [
            { value: 'report_a_bug', label: 'Report a Bug' },
            { value: 'guidance', label: 'Guidance Center' },
          ],
          value: ['guidance', 'retired_assistant'],
        },
      },
    },
  ],
};

export default docs;
